// Booking validation utilities

export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePhone = (phone) => {
  if (!phone) return false;
  
  // Remove all non-digit characters except leading +
  const cleaned = phone.replace(/[^\d+]/g, '');
  
  // Remove + for digit count
  const digitsOnly = cleaned.replace(/\+/g, '');
  
  // UK phone numbers validation
  // Mobile: 07xxx xxxxxx (11 digits) or +447xxx xxxxxx (13 digits with +44)
  // Landline: 01xxx/02xxx/03xxx (10-11 digits) or +441xxx/+442xxx/+443xxx (12-13 digits with +44)
  
  // Check for UK mobile with +44
  if (cleaned.startsWith('+447')) {
    return digitsOnly.length === 12; // +447xxxxxxxxx = 12 digits
  }
  
  // Check for UK mobile without country code
  if (cleaned.startsWith('07')) {
    return digitsOnly.length === 11; // 07xxxxxxxxx = 11 digits
  }
  
  // Check for UK landline with +44
  if (cleaned.startsWith('+44')) {
    return digitsOnly.length >= 11 && digitsOnly.length <= 12; // Landlines can vary
  }
  
  // Check for UK landline without country code (01, 02, 03)
  if (cleaned.match(/^0[123]/)) {
    return digitsOnly.length >= 10 && digitsOnly.length <= 11;
  }
  
  // International numbers (basic validation)
  if (cleaned.startsWith('+')) {
    return digitsOnly.length >= 10 && digitsOnly.length <= 15;
  }
  
  // Fallback: any number with at least 10 digits
  return digitsOnly.length >= 10 && digitsOnly.length <= 15;
};

export const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  
  // Remove all non-digit characters except leading +
  const cleaned = phone.replace(/[^\d+]/g, '');
  const digitsOnly = cleaned.replace(/\+/g, '');
  
  // Format UK mobile: 07xxx xxxxxx
  if (cleaned.startsWith('07') && digitsOnly.length === 11) {
    return cleaned.replace(/^(07\d{3})(\d{6})$/, '$1 $2');
  }
  
  // Format UK mobile with +44: +44 7xxx xxxxxx
  if (cleaned.startsWith('+447') && digitsOnly.length === 12) {
    return cleaned.replace(/^\+44(7\d{3})(\d{6})$/, '+44 $1 $2');
  }
  
  // Format UK landline: 01xxx xxxxxx / 02x xxxx xxxx
  if (cleaned.match(/^0[123]/) && digitsOnly.length >= 10) {
    if (cleaned.startsWith('02')) {
      // London and other major cities: 02x xxxx xxxx
      return cleaned.replace(/^(0\d{2})(\d{4})(\d{4})$/, '$1 $2 $3');
    } else {
      // Other areas: 01xxx xxxxxx
      return cleaned.replace(/^(0\d{4})(\d{6})$/, '$1 $2') || 
             cleaned.replace(/^(0\d{3})(\d{3})(\d{4})$/, '$1 $2 $3');
    }
  }
  
  // Return cleaned number if no specific format matches
  return cleaned;
};

export const validatePostcode = (postcode) => {
  if (!postcode) return false;
  
  // Remove all spaces and convert to uppercase for validation
  const cleaned = postcode.replace(/\s+/g, '').toUpperCase();
  
  // UK postcode regex without space requirement
  // Matches patterns like: SW1A1AA, B338TH, CV230SS, etc.
  const ukPostcodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]?[0-9][A-Z]{2}$/i;
  
  // Check length (UK postcodes are 5-7 characters without spaces)
  if (cleaned.length < 5 || cleaned.length > 7) {
    return false;
  }
  
  return ukPostcodeRegex.test(cleaned);
};

export const validateCardNumber = (cardNumber) => {
  // Remove spaces and non-digits
  const cleaned = cardNumber.replace(/\D/g, '');
  
  // Check if it's a valid length (13-19 digits)
  if (cleaned.length < 13 || cleaned.length > 19) {
    return false;
  }
  
  // Luhn algorithm
  let sum = 0;
  let isEven = false;
  
  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = parseInt(cleaned[i]);
    
    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }
    
    sum += digit;
    isEven = !isEven;
  }
  
  return sum % 10 === 0;
};

export const validateCVV = (cvv) => {
  const cvvRegex = /^[0-9]{3,4}$/;
  return cvvRegex.test(cvv);
};

export const validateExpiryDate = (month, year) => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  
  const expiryYear = parseInt(year);
  const expiryMonth = parseInt(month);
  
  if (expiryYear < currentYear) {
    return false;
  }
  
  if (expiryYear === currentYear && expiryMonth < currentMonth) {
    return false;
  }
  
  return true;
};

export const validateCustomerDetails = (customerDetails) => {
  const errors = {};
  
  if (!customerDetails.firstName?.trim()) {
    errors.firstName = 'First name is required';
  }
  
  if (!customerDetails.lastName?.trim()) {
    errors.lastName = 'Last name is required';
  }
  
  const trimmedEmail = customerDetails.email?.trim();
  const trimmedConfirmEmail = customerDetails.confirmEmail?.trim();

  if (!trimmedEmail) {
    errors.email = 'Email is required';
  } else if (!validateEmail(trimmedEmail)) {
    errors.email = 'Please enter a valid email address';
  }

  if (!trimmedConfirmEmail) {
    errors.confirmEmail = 'Please confirm your email address';
  } else if (trimmedEmail && trimmedConfirmEmail && trimmedEmail.toLowerCase() !== trimmedConfirmEmail.toLowerCase()) {
    errors.confirmEmail = 'Email addresses do not match';
  }
  
  if (!customerDetails.phone?.trim()) {
    errors.phone = 'Phone number is required';
  } else if (!validatePhone(customerDetails.phone)) {
    errors.phone = 'Please enter a valid UK phone number (e.g., 07123 456789 or +44 7123 456789)';
  }
  
  return errors;
};

export const validateAddress = (address) => {
  const errors = {};

  if (!address.houseNumber?.trim()) {
    errors.houseNumber = 'House/building name or number is required';
  }

  if (!address.line1?.trim()) {
    errors.line1 = 'Address line 1 is required';
  }

  if (!address.city?.trim()) {
    errors.city = 'City is required';
  }

  if (!address.postcode?.trim()) {
    errors.postcode = 'Postcode is required';
  } else if (!validatePostcode(address.postcode)) {
    errors.postcode = 'Please enter a valid UK postcode';
  }

  return errors;
};

export const validatePaymentDetails = (paymentDetails) => {
  const errors = {};
  
  if (paymentDetails.method === 'card') {
    const cardDetails = paymentDetails.cardDetails;
    
    if (!cardDetails.number?.trim()) {
      errors.cardNumber = 'Card number is required';
    } else if (!validateCardNumber(cardDetails.number)) {
      errors.cardNumber = 'Please enter a valid card number';
    }
    
    if (!cardDetails.expiryMonth) {
      errors.expiryMonth = 'Expiry month is required';
    }
    
    if (!cardDetails.expiryYear) {
      errors.expiryYear = 'Expiry year is required';
    }
    
    if (cardDetails.expiryMonth && cardDetails.expiryYear) {
      if (!validateExpiryDate(cardDetails.expiryMonth, cardDetails.expiryYear)) {
        errors.expiryDate = 'Card has expired';
      }
    }
    
    if (!cardDetails.cvv?.trim()) {
      errors.cvv = 'CVV is required';
    } else if (!validateCVV(cardDetails.cvv)) {
      errors.cvv = 'Please enter a valid CVV';
    }
    
    if (!cardDetails.name?.trim()) {
      errors.cardName = 'Name on card is required';
    }
  }
  
  return errors;
};

export const validateBookingStep = (step, bookingState) => {
  const errors = {};
  
  switch (step) {
    case 1: // Ticket selection
      if (!bookingState.selectedTickets || bookingState.selectedTickets.length === 0) {
        errors.tickets = 'Please select at least one ticket';
      } else {
        const hasValidQuantity = bookingState.selectedTickets.some(ticket => ticket.quantity > 0);
        if (!hasValidQuantity) {
          errors.tickets = 'Please select at least one ticket';
        }
      }
      break;
      
    case 2: // Date selection
      if (!bookingState.selectedDate) {
        errors.date = 'Please select a visit date';
      }
      break;
      
    case 3: // Customer details
      const customerErrors = validateCustomerDetails(bookingState.customerDetails);
      const addressErrors = validateAddress(bookingState.customerDetails.address);
      return { ...customerErrors, ...addressErrors };
      
    case 4: // Payment
      return validatePaymentDetails(bookingState.paymentDetails);
      
    default:
      break;
  }
  
  return errors;
};

export const formatCardNumber = (value) => {
  // Remove all non-digits
  const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
  
  // Add spaces every 4 digits
  const matches = v.match(/\d{4,16}/g);
  const match = matches && matches[0] || '';
  const parts = [];
  
  for (let i = 0, len = match.length; i < len; i += 4) {
    parts.push(match.substring(i, i + 4));
  }
  
  if (parts.length) {
    return parts.join(' ');
  } else {
    return v;
  }
};

export const formatExpiryDate = (value) => {
  // Remove all non-digits
  const v = value.replace(/\D/g, '');
  
  // Add slash after 2 digits
  if (v.length >= 2) {
    return v.substring(0, 2) + '/' + v.substring(2, 4);
  }
  
  return v;
};

export const getCardType = (cardNumber) => {
  const cleaned = cardNumber.replace(/\D/g, '');
  
  if (/^4/.test(cleaned)) {
    return 'visa';
  } else if (/^5[1-5]/.test(cleaned)) {
    return 'mastercard';
  } else if (/^3[47]/.test(cleaned)) {
    return 'amex';
  } else if (/^6/.test(cleaned)) {
    return 'discover';
  }
  
  return 'unknown';
};

export default {
  validateEmail,
  validatePhone,
  validatePostcode,
  validateCardNumber,
  validateCVV,
  validateExpiryDate,
  validateCustomerDetails,
  validateAddress,
  validatePaymentDetails,
  validateBookingStep,
  formatCardNumber,
  formatExpiryDate,
  formatPhoneNumber,
  getCardType
};